import {NotFoundError} from '@packages/utils';
import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import {
  type GetEntrySheetResponse,
  getEntrySheet,
} from '../../core/repositories/reportsRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {EntrySheetPage} from '../../features/reports/components/EntrySheetPage';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../_app';

type Props = {
  isLogin: boolean;
  entrySheet: GetEntrySheetResponse;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;

    const {id: entrySheetId} = context.params as {id: string};
    const result = await getEntrySheet({
      entrySheetID: Number(entrySheetId),
      cookies,
    });

    if (!result.ok) {
      if (result.error === NotFoundError) {
        return {
          notFound: true,
        };
      }

      throw new Error(result.error);
    }

    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {
          isLogin: false,
          entrySheet: result.value,
        },
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props: {
          isLogin: false,
          entrySheet: result.value,
        },
      };
    }

    return {
      props: {
        isLogin: true,
        userDataForAnalytics: userDataResponse.value,
        entrySheet: result.value,
      },
    };
  },
);

const PassedES: NextPageWithLayout<Props> = ({
  isLogin,
  entrySheet,
  userDataForAnalytics,
}) => {
  return (
    <>
      <EntrySheetPage isLogin={isLogin} entrySheet={entrySheet} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

PassedES.getLayout = (page) => (
  <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>
);

export default PassedES;
